import React, { Component } from 'react'
import { operations } from 'types'

import Icon, { IconName } from './Icon'

interface State {
  isHorizontallyScrollable: boolean
  isScrolledToEnd: boolean
}

interface Props {
  keys: string[]
  rows: { id: any; values: any[]; operations?: operations[]; selectable?: boolean }[]
  onOperationClick?: (operation: operations, recordId: any) => any
  onPageSelectionChange?: (recordIds: any[], selected: boolean) => void
  onRowSelectionChange?: (recordId: any, selected: boolean) => void
  selectedRowIds?: any[]
}

const operationIcons: Record<operations, IconName> = {
  Modifica: 'edit',
  Cancella: 'delete',
  Conferma: 'approve',
  'Conferma Modifica': 'approve',
  Assegna: 'assign',
  Approva: 'approve',
  Rinvia: 'close',
  Dettaglio: 'detail',
  Accesso: 'access',
  Sincronizza: 'sync',
}

export default class Table extends Component<Props, State> {
  state: State = {
    isHorizontallyScrollable: false,
    isScrolledToEnd: true,
  }

  private tableShell?: HTMLDivElement

  componentDidMount() {
    window.addEventListener('resize', this.updateScrollState)
    this.updateScrollState()
  }

  componentDidUpdate(previousProps: Props) {
    if (previousProps.rows !== this.props.rows || previousProps.keys !== this.props.keys) {
      this.updateScrollState()
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateScrollState)
  }

  updateScrollState = () => {
    if (!this.tableShell) return

    const { clientWidth, scrollLeft, scrollWidth } = this.tableShell
    const isHorizontallyScrollable = scrollWidth > clientWidth + 1
    const isScrolledToEnd = !isHorizontallyScrollable || scrollLeft >= scrollWidth - clientWidth - 1

    if (
      isHorizontallyScrollable !== this.state.isHorizontallyScrollable ||
      isScrolledToEnd !== this.state.isScrolledToEnd
    ) {
      this.setState({ isHorizontallyScrollable, isScrolledToEnd })
    }
  }

  renderRows() {
    const { onOperationClick, onRowSelectionChange, selectedRowIds = [] } = this.props
    const selectedIds = new Set(selectedRowIds.map(String))
    return this.props.rows.map((row) => (
      <Row
        id={row.id}
        key={String(row.id)}
        values={row.values}
        operations={row.operations}
        onOperationClick={onOperationClick}
        onSelectionChange={onRowSelectionChange}
        selectable={row.selectable !== false}
        selected={selectedIds.has(String(row.id))}
      />
    ))
  }

  renderSelectionHeader() {
    const { onPageSelectionChange, onRowSelectionChange, rows, selectedRowIds = [] } = this.props
    if (!onRowSelectionChange) return null

    const selectableIds = rows.filter((row) => row.selectable !== false).map((row) => row.id)
    const selectedIds = new Set(selectedRowIds.map(String))
    const selectedOnPage = selectableIds.filter((id) => selectedIds.has(String(id))).length
    const allSelected = selectableIds.length > 0 && selectedOnPage === selectableIds.length
    const partiallySelected = selectedOnPage > 0 && !allSelected

    return (
      <th className="sq-table__selection" scope="col">
        <input
          aria-label="Seleziona tutte le domande assegnabili in questa pagina"
          checked={allSelected}
          disabled={selectableIds.length === 0}
          onChange={(event) => onPageSelectionChange && onPageSelectionChange(selectableIds, event.target.checked)}
          ref={(input) => {
            if (input) input.indeterminate = partiallySelected
          }}
          type="checkbox"
        />
      </th>
    )
  }

  render() {
    const { onOperationClick } = this.props
    const { isHorizontallyScrollable, isScrolledToEnd } = this.state
    if (this.props.rows.length === 0) {
      return (
        <div className="sq-empty-state" role="status">
          <strong>Nessun risultato</strong>
          <span>Prova a modificare i filtri o la ricerca.</span>
        </div>
      )
    }

    const tableShellClassName = [
      'sq-table-shell',
      isHorizontallyScrollable ? 'sq-table-shell--scrollable' : '',
      isScrolledToEnd ? 'sq-table-shell--at-end' : '',
    ].filter(Boolean).join(' ')

    return (
      <div
        className={tableShellClassName}
        onScroll={this.updateScrollState}
        ref={(element) => { this.tableShell = element || undefined }}
      >
        <table className="sq-table">
          <thead>
            <tr>
              {this.renderSelectionHeader()}
              {this.props.keys.map((key) => <th key={key} scope="col">{key}</th>)}
              {onOperationClick && <th className="sq-table__actions" scope="col">Operazioni</th>}
            </tr>
          </thead>
          <tbody>{this.renderRows()}</tbody>
        </table>
      </div>
    )
  }
}

interface RowProps {
  id: any
  values: any[]
  operations?: operations[]
  onOperationClick?: (operation: operations, recordId: any) => any
  onSelectionChange?: (recordId: any, selected: boolean) => void
  selectable: boolean
  selected: boolean
}

const Row = ({
  id,
  values,
  operations: rowOperations,
  onOperationClick,
  onSelectionChange,
  selectable,
  selected,
}: RowProps) => (
  <tr>
    {onSelectionChange && (
      <td className="sq-table__selection">
        <input
          aria-label={`Seleziona la domanda ${String(id)}`}
          checked={selected}
          disabled={!selectable}
          onChange={(event) => onSelectionChange(id, event.target.checked)}
          type="checkbox"
        />
      </td>
    )}
    {values.map((value, index) => <td key={`${String(id)}-${index}`}>{value}</td>)}
    {onOperationClick && (
      <td className="sq-table__actions">
        {rowOperations?.map((operation) => (
          <button
            aria-label={operation}
            className={`sq-icon-button${operation === 'Cancella' ? ' sq-icon-button--danger' : ''}`}
            key={operation}
            onClick={() => onOperationClick(operation, id)}
            title={operation}
            type="button"
          >
            <Icon name={operationIcons[operation]} size={18} />
          </button>
        ))}
      </td>
    )}
  </tr>
)
